import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { isSalesDashboardSharedRoute } from '@/constants/salesDashboardNavigation'
import { getDashboardPagesPerformanceFilterOptions } from '@/services/salesDashboardPagesService'
import type { SdPerformanceScope } from '@/types/salesDashboardSession'
import {
  SALES_DASHBOARD_EMPTY_CUSTOM_RANGE,
  SALES_DASHBOARD_EMPTY_FILTERS,
  type SalesDashboardCustomRange,
  type SalesDashboardFilters,
  type SalesFilterOptions,
  type SalesMode,
  type SalesTimeRangePreset,
} from '@/types/salesDashboardShared'
import {
  buildDefaultSalesDashboardFilters,
  canonicalizeRepKeys,
  cloneSalesDashboardFilters,
  countAppliedSalesDashboardFilters,
  getDefaultSalesDashboardCustomRange,
  isAppliedTimeRangeNonDefault,
  mapSalesFilterOptionsFromApi,
} from '@/utils/salesDashboardSharedFilters'
import type { SalesDashboardStatusOptions } from '@/utils/salesDashboardStatusDefaults'
import { getTodayOtsDateString } from '@/utils/datePresets'
import { monthKeyFromDateString } from '@/utils/monthKey'
import {
  clearPersistedSalesDashboardSession,
  loadPersistedSalesDashboardSession,
  reconcilePersistedFiltersWithOptions,
  savePersistedSalesDashboardSession,
} from '@/utils/salesDashboardSessionPersistence'

export const useSalesDashboardPagesStore = defineStore('salesDashboardPages', () => {
  const persistedSession = loadPersistedSalesDashboardSession()
  const sessionRestoredFromPersistence = persistedSession != null

  const timeRange = ref<SalesTimeRangePreset>(persistedSession?.timeRange ?? 'month')
  const customRange = ref<SalesDashboardCustomRange>(
    persistedSession?.customRange ?? { ...SALES_DASHBOARD_EMPTY_CUSTOM_RANGE }
  )
  const draftTimeRange = ref<SalesTimeRangePreset>(timeRange.value)
  const draftCustomRange = ref<SalesDashboardCustomRange>({ ...customRange.value })
  const selectedMonth = ref(
    persistedSession?.selectedMonth ?? monthKeyFromDateString(getTodayOtsDateString())
  )
  const salesMode = ref<SalesMode>(persistedSession?.salesMode ?? 'created')
  const performanceScope = ref<SdPerformanceScope>(persistedSession?.performanceScope ?? 'org')

  const activeFilters = ref<SalesDashboardFilters>(
    cloneSalesDashboardFilters(persistedSession?.activeFilters ?? SALES_DASHBOARD_EMPTY_FILTERS)
  )
  const draftFilters = ref<SalesDashboardFilters>(
    cloneSalesDashboardFilters(SALES_DASHBOARD_EMPTY_FILTERS)
  )
  const drawerOpen = ref(false)
  const filtersReady = ref(false)
  /** True while the rep filter was set by a drill-down (e.g. leaderboard rep click), not by the user. */
  const repKeysDrillApplied = ref(false)
  /** Rep keys that were applied before the drill, restored when the drill filter is lifted. */
  let repKeysBeforeDrill: string[] = []

  const filterOptions = ref<SalesFilterOptions | null>(null)
  const filterOptionsLoading = ref(false)
  const filterOptionsLoaded = ref(false)
  let filterOptionsLoadStarted = false
  let sessionDefaultsInitialized = false
  let filterOptionsAbortController: AbortController | null = null
  let filterOptionsInFlight: Promise<void> | null = null
  let persistEnabled = true

  const statusOptionsForBadge = computed((): SalesDashboardStatusOptions | null => {
    if (!filterOptions.value) return null
    return {
      order: [...filterOptions.value.orderStatuses],
      product: [...filterOptions.value.productStatuses],
      invoice: [...filterOptions.value.invoiceStatuses],
    }
  })

  const defaultFilters = computed((): SalesDashboardFilters => {
    if (!filterOptions.value) {
      return cloneSalesDashboardFilters(SALES_DASHBOARD_EMPTY_FILTERS)
    }
    return buildDefaultSalesDashboardFilters(filterOptions.value)
  })

  const appliedFilterCount = computed(() => {
    let count = countAppliedSalesDashboardFilters(
      activeFilters.value,
      statusOptionsForBadge.value ?? undefined
    )
    if (isAppliedTimeRangeNonDefault(timeRange.value, customRange.value)) {
      count += 1
    }
    return count
  })

  const statusDependentFiltersPending = computed(
    () => filterOptionsLoading.value || !filterOptionsLoaded.value
  )

  function persistAppliedSession(): void {
    if (!persistEnabled || !filterOptionsLoaded.value) return
    savePersistedSalesDashboardSession({
      timeRange: timeRange.value,
      customRange: customRange.value,
      selectedMonth: selectedMonth.value,
      salesMode: salesMode.value,
      performanceScope: performanceScope.value,
      activeFilters: activeFilters.value,
    })
  }

  watch(
    [timeRange, customRange, selectedMonth, salesMode, performanceScope, activeFilters],
    () => {
      persistAppliedSession()
    },
    { deep: true }
  )

  function initializeSessionDefaultsFromMetadata(options: SalesFilterOptions): void {
    filterOptions.value = {
      orderStatuses: [...options.orderStatuses],
      productStatuses: [...options.productStatuses],
      invoiceStatuses: [...options.invoiceStatuses],
      glCodes: [...options.glCodes],
      glNames: [...options.glNames],
      customerStates: [...options.customerStates],
      customerStatesByCountry: { ...options.customerStatesByCountry },
      accountGroups: [...options.accountGroups],
      salesReps: [...options.salesReps],
    }
    if (!sessionDefaultsInitialized) {
      const persistedLooksInitialized =
        sessionRestoredFromPersistence &&
        (activeFilters.value.orderStatuses.length > 0 ||
          activeFilters.value.productStatuses.length > 0 ||
          activeFilters.value.invoiceStatuses.length > 0)

      if (persistedLooksInitialized) {
        const reconciled = reconcilePersistedFiltersWithOptions(activeFilters.value, options)
        activeFilters.value = reconciled
        draftFilters.value = cloneSalesDashboardFilters(reconciled)
      } else {
        const defaults = buildDefaultSalesDashboardFilters(options)
        // Filters can already be set before the options request resolves (e.g. a
        // leaderboard drill-down applies a rep filter). Keep those selections
        // instead of clobbering them with defaults, and canonicalize rep keys now
        // that the rep options are known.
        const withEarlySelections = (current: SalesDashboardFilters): SalesDashboardFilters => ({
          ...cloneSalesDashboardFilters(defaults),
          teamUuids: [...current.teamUuids],
          teamMemberUuids: [...current.teamMemberUuids],
          repKeys: canonicalizeRepKeys(current.repKeys, options.salesReps),
          customerStates: [...current.customerStates],
          accountGroups: [...current.accountGroups],
        })
        activeFilters.value = withEarlySelections(activeFilters.value)
        draftFilters.value = withEarlySelections(draftFilters.value)
      }
      sessionDefaultsInitialized = true
    }
    filtersReady.value = true
  }

  function markFiltersReady(): void {
    filtersReady.value = true
  }

  async function loadFilterOptionsOnce(): Promise<void> {
    if (filterOptionsLoaded.value) return
    if (filterOptionsInFlight) return filterOptionsInFlight

    filterOptionsLoadStarted = true
    filterOptionsLoading.value = true

    filterOptionsAbortController?.abort()
    const controller = new AbortController()
    filterOptionsAbortController = controller

    filterOptionsInFlight = (async () => {
      try {
        const result = await getDashboardPagesPerformanceFilterOptions(controller.signal)
        if (controller.signal.aborted) return
        if (result.ok) {
          const options = mapSalesFilterOptionsFromApi(result.data)
          filterOptionsLoaded.value = true
          initializeSessionDefaultsFromMetadata(options)
          return
        }
        if (result.status === 0) return
        markFiltersReady()
      } catch {
        if (!controller.signal.aborted) {
          markFiltersReady()
        }
      } finally {
        filterOptionsLoading.value = false
        if (filterOptionsAbortController === controller) {
          filterOptionsAbortController = null
        }
        filterOptionsInFlight = null
      }
    })()

    return filterOptionsInFlight
  }

  function resetSessionToDefaults(): void {
    persistEnabled = false
    timeRange.value = 'month'
    customRange.value = { ...SALES_DASHBOARD_EMPTY_CUSTOM_RANGE }
    draftTimeRange.value = 'month'
    draftCustomRange.value = { ...SALES_DASHBOARD_EMPTY_CUSTOM_RANGE }
    selectedMonth.value = monthKeyFromDateString(getTodayOtsDateString())
    salesMode.value = 'created'
    performanceScope.value = 'org'
    drawerOpen.value = false

    const defaults = defaultFilters.value
    activeFilters.value = cloneSalesDashboardFilters(defaults)
    draftFilters.value = cloneSalesDashboardFilters(defaults)
    repKeysDrillApplied.value = false
    repKeysBeforeDrill = []
    clearPersistedSalesDashboardSession()
    persistEnabled = true
  }

  function syncTimeDraftFromActive(): void {
    draftTimeRange.value = timeRange.value
    draftCustomRange.value = { ...customRange.value }
  }

  function applyTimeDraftToApplied(): void {
    setTimeRange(draftTimeRange.value)
    setCustomRange(draftCustomRange.value)
  }

  function syncDraftFromActive(): void {
    draftFilters.value = cloneSalesDashboardFilters(activeFilters.value)
    syncTimeDraftFromActive()
  }

  function openDrawer(): void {
    syncDraftFromActive()
    drawerOpen.value = true
  }

  /** Opens the filter drawer with draft preset Custom without changing the applied range until Apply. */
  function openDrawerWithCustomDateRange(): void {
    syncDraftFromActive()
    setDraftTimeRange('custom')
    if (!draftCustomRange.value.from && !draftCustomRange.value.to) {
      if (customRange.value.from || customRange.value.to) {
        draftCustomRange.value = { ...customRange.value }
      }
    }
    drawerOpen.value = true
  }

  function applyDraft(): void {
    const repKeysChanged =
      draftFilters.value.repKeys.length !== activeFilters.value.repKeys.length ||
      draftFilters.value.repKeys.some((key, i) => key !== activeFilters.value.repKeys[i])
    activeFilters.value = cloneSalesDashboardFilters(draftFilters.value)
    applyTimeDraftToApplied()
    drawerOpen.value = false
    if (repKeysChanged) {
      repKeysDrillApplied.value = false
      repKeysBeforeDrill = []
    }
  }

  function discardDraft(): void {
    syncDraftFromActive()
    drawerOpen.value = false
  }

  function resetDraft(): void {
    draftFilters.value = cloneSalesDashboardFilters(defaultFilters.value)
    draftTimeRange.value = 'month'
    draftCustomRange.value = { ...SALES_DASHBOARD_EMPTY_CUSTOM_RANGE }
  }

  function setSalesMode(mode: SalesMode): void {
    salesMode.value = mode
  }

  function setTimeRange(range: SalesTimeRangePreset): void {
    timeRange.value = range
    if (range === 'custom' && !customRange.value.from && !customRange.value.to) {
      customRange.value = getDefaultSalesDashboardCustomRange()
    }
  }

  function setCustomRange(range: SalesDashboardCustomRange): void {
    customRange.value = { ...range }
  }

  function setDraftTimeRange(range: SalesTimeRangePreset): void {
    draftTimeRange.value = range
    if (range === 'custom' && !draftCustomRange.value.from && !draftCustomRange.value.to) {
      draftCustomRange.value = getDefaultSalesDashboardCustomRange()
    }
  }

  function setDraftCustomRange(range: SalesDashboardCustomRange): void {
    draftCustomRange.value = { ...range }
  }

  function getDraftComparableState(): unknown {
    return {
      filters: draftFilters.value,
      timeRange: draftTimeRange.value,
      customRange: draftCustomRange.value,
    }
  }

  type PageBodyFilterFields = Pick<
    SalesDashboardFilters,
    'teamUuids' | 'teamMemberUuids' | 'repKeys' | 'customerStates' | 'accountGroups'
  >

  function applyPageBodyFilters(fields: PageBodyFilterFields): void {
    const nextActive = {
      ...cloneSalesDashboardFilters(activeFilters.value),
      teamUuids: [...fields.teamUuids],
      teamMemberUuids: [...fields.teamMemberUuids],
      repKeys: [...fields.repKeys],
      customerStates: [...fields.customerStates],
      accountGroups: [...fields.accountGroups],
    }
    activeFilters.value = nextActive
    draftFilters.value = {
      ...cloneSalesDashboardFilters(draftFilters.value),
      teamUuids: [...fields.teamUuids],
      teamMemberUuids: [...fields.teamMemberUuids],
      repKeys: [...fields.repKeys],
      customerStates: [...fields.customerStates],
      accountGroups: [...fields.accountGroups],
    }
    repKeysDrillApplied.value = false
    repKeysBeforeDrill = []
  }

  /**
   * Apply a rep filter from a drill-down click (e.g. leaderboard rep name).
   * The key is canonicalized against the loaded filter options so the rep
   * dropdown recognizes the selection. The previous rep filter is snapshotted
   * and restored when the user navigates away from the drill target page.
   */
  function applyRepDrillFilter(repKey: string): void {
    const previousRepKeys = [...activeFilters.value.repKeys]
    applyPageBodyFilters({
      teamUuids: [...activeFilters.value.teamUuids],
      teamMemberUuids: [...activeFilters.value.teamMemberUuids],
      repKeys: canonicalizeRepKeys([repKey], filterOptions.value?.salesReps ?? []),
      customerStates: [...activeFilters.value.customerStates],
      accountGroups: [...activeFilters.value.accountGroups],
    })
    repKeysBeforeDrill = previousRepKeys
    repKeysDrillApplied.value = true
  }

  /**
   * Lift a drill-applied rep filter, restoring whatever rep filter was active
   * before the drill (no-op when the rep filter was set manually).
   */
  function restorePreDrillRepFilter(): void {
    if (!repKeysDrillApplied.value) return
    const restored = [...repKeysBeforeDrill]
    activeFilters.value = {
      ...cloneSalesDashboardFilters(activeFilters.value),
      repKeys: [...restored],
    }
    draftFilters.value = {
      ...cloneSalesDashboardFilters(draftFilters.value),
      repKeys: [...restored],
    }
    repKeysBeforeDrill = []
    repKeysDrillApplied.value = false
  }

  return {
    timeRange,
    customRange,
    draftTimeRange,
    draftCustomRange,
    selectedMonth,
    salesMode,
    performanceScope,
    sessionRestoredFromPersistence,
    activeFilters,
    draftFilters,
    drawerOpen,
    filtersReady,
    repKeysDrillApplied,
    filterOptions,
    filterOptionsLoading,
    filterOptionsLoaded,
    statusOptionsForBadge,
    defaultFilters,
    appliedFilterCount,
    statusDependentFiltersPending,
    loadFilterOptionsOnce,
    resetSessionToDefaults,
    syncDraftFromActive,
    openDrawer,
    openDrawerWithCustomDateRange,
    applyDraft,
    discardDraft,
    resetDraft,
    setSalesMode,
    setTimeRange,
    setCustomRange,
    setDraftTimeRange,
    setDraftCustomRange,
    getDraftComparableState,
    markFiltersReady,
    initializeSessionDefaultsFromMetadata,
    applyPageBodyFilters,
    applyRepDrillFilter,
    restorePreDrillRepFilter,
  }
})

export function handleSalesDashboardNavigation(
  toName: string | symbol | null | undefined,
  fromName: string | symbol | null | undefined
): void {
  const store = useSalesDashboardPagesStore()

  if (isSalesDashboardSharedRoute(fromName) && !isSalesDashboardSharedRoute(toName)) {
    store.drawerOpen = false
  }

  if (!isSalesDashboardSharedRoute(toName)) return
  void store.loadFilterOptionsOnce()

  if (fromName != null && !isSalesDashboardSharedRoute(fromName)) {
    store.resetSessionToDefaults()
    return
  }

  // A rep filter applied by drill-down is scoped to the drill target page;
  // restore the pre-drill rep filter when moving on to another shared dashboard page.
  if (fromName === 'SalesSaleDetail' && toName !== 'SalesSaleDetail') {
    store.restorePreDrillRepFilter()
  }
}
